import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PreviewTemplate from 'components/common/previewTemplate';
import TemplateStatusTag, { isTemplateSelectable } from 'components/templates/templateStatus';
import { type FC, useMemo, useState } from 'react';
import { useGetBusinessUnitTemplatesQuery } from 'services/api/businessUnits.api';
import type { FollowUpConfigDraft } from 'services/types/followUpConfig';
import type { TemplateResponse } from 'services/types/message';

/**
 * Maps a named param to a client attribute based on common parameter names
 */
const getClientAttributeForParam = (param: { param_name: string; example: string }): string => {
  const lowerParamName = param.param_name.toLowerCase();

  if (['client', 'cliente', 'nombre', 'nombre_cliente', 'name'].includes(lowerParamName)) {
    return 'client.firstName';
  }
  if (['brand', 'marca', 'brand_of_interest'].includes(lowerParamName)) {
    return 'client.brandOfInterest';
  }
  if (['model', 'modelo', 'auto', 'car', 'model_of_interest'].includes(lowerParamName)) {
    return 'client.modelsOfInterest';
  }
  if (['branch_office', 'sucursal', 'branch'].includes(lowerParamName)) {
    return 'client.branchOffice';
  }
  if (['email', 'correo'].includes(lowerParamName)) {
    return 'client.email';
  }
  if (['phone', 'telefono', 'celular'].includes(lowerParamName)) {
    return 'client.phone';
  }

  // Default: return the example value from the template
  return param.example;
};

/**
 * Generates templateComponents JSON based on the selected template
 */
const generateTemplateComponents = (template: TemplateResponse[number] | undefined): string | null => {
  if (!template) return null;
  const bodyComponent = template.components.find((component) => component.type === 'BODY');
  if (!bodyComponent?.example) return null;

  // Handle named params format
  if (bodyComponent.example.body_text_named_params) {
    return JSON.stringify(
      {
        bodyComponents: bodyComponent.example.body_text_named_params.map((param) => ({
          paramName: param.param_name,
          value: getClientAttributeForParam(param),
        })),
      },
      null,
      2
    );
  }

  // Handle positional params format
  if (bodyComponent.example.body_text) {
    return JSON.stringify(
      {
        bodyComponents: bodyComponent.example.body_text[0],
      },
      null,
      2
    );
  }

  return null;
};

interface TemplatesSectionProps {
  followUpConfigDraft: FollowUpConfigDraft;
  isEditing: boolean;
  isCampaign: boolean;
  businessUnitId?: number;
  setFollowUpConfigDraft?: (followUpConfigDraft: FollowUpConfigDraft) => void;
}

const TemplatesSection: FC<TemplatesSectionProps> = ({
  followUpConfigDraft,
  isEditing,
  isCampaign,
  businessUnitId,
  setFollowUpConfigDraft,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>(followUpConfigDraft.templateName || '');

  // Use query hook instead of mutation for automatic caching
  const { data: templatesData = [], isLoading: isLoadingTemplates } = useGetBusinessUnitTemplatesQuery(
    businessUnitId ?? 0,
    {
      skip: !businessUnitId,
    }
  );

  // Memoize sorted templates to avoid re-sorting on every render
  const templates = useMemo(() => {
    return [...templatesData].sort((a, b) => a.name.localeCompare(b.name));
  }, [templatesData]);

  const handleTemplateSelection = (templateName: string) => {
    setSelectedTemplate(templateName);
    if (setFollowUpConfigDraft) {
      const selectedTemplateObj = templates.find((t) => t.name === templateName);
      const generatedComponents = generateTemplateComponents(selectedTemplateObj);

      setFollowUpConfigDraft({
        ...followUpConfigDraft,
        templateName: templateName || null,
        templateComponents: generatedComponents,
      });
    }
  };

  if (
    followUpConfigDraft.templateName === null &&
    !(
      followUpConfigDraft.prompt !== null &&
      followUpConfigDraft.templatesToChoose &&
      followUpConfigDraft.templatesToChoose.length > 0
    )
  ) {
    return null;
  }

  const selectedTemplateData = templates.find((t) => t.name === followUpConfigDraft.templateName);

  // Parse templateComponents to get variable examples
  let bodyVariableExamples: string[] | null = null;
  if (selectedTemplateData && followUpConfigDraft.templateComponents) {
    try {
      const parsed: unknown = JSON.parse(followUpConfigDraft.templateComponents);
      if (Array.isArray(parsed)) {
        bodyVariableExamples = parsed as string[];
      } else if (typeof parsed === 'object' && parsed !== null) {
        const { bodyComponents } = parsed as { bodyComponents?: unknown };
        bodyVariableExamples = Array.isArray(bodyComponents)
          ? (bodyComponents as unknown[]).filter((v): v is string => typeof v === 'string')
          : Object.values(parsed as Record<string, unknown>).map(String);
      }
    } catch {
      bodyVariableExamples =
        selectedTemplateData.components.find((c) => c.type === 'BODY')?.example?.body_text?.[0] || null;
    }
  } else if (selectedTemplateData) {
    bodyVariableExamples =
      selectedTemplateData.components.find((c) => c.type === 'BODY')?.example?.body_text?.[0] || null;
  }

  return (
    <Card sx={{ mb: 1.5, boxShadow: 0, borderRadius: 0 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
          Templates
        </Typography>
        {followUpConfigDraft.templateName !== null && !isCampaign && (
          <>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: 'text.secondary',
                }}
              >
                Template seleccionado:
              </Typography>
              {isEditing ? (
                <FormControl fullWidth>
                  <InputLabel>Plantillas disponibles</InputLabel>
                  <Select
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateSelection(e.target.value)}
                    label="Plantillas disponibles"
                  >
                    <MenuItem value="">
                      <em>Seleccionar una plantilla</em>
                    </MenuItem>
                    {templates.map((template) => (
                      <MenuItem
                        key={template.id}
                        value={template.name}
                        disabled={!isTemplateSelectable(template.status)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}
                      >
                        <span>
                          {template.name} ({template.language})
                        </span>
                        <TemplateStatusTag status={template.status} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {followUpConfigDraft.templateName}
                  {isLoadingTemplates
                    ? ' (Cargando...)'
                    : templates.find((t) => t.name === followUpConfigDraft.templateName) &&
                      ` (${templates.find((t) => t.name === followUpConfigDraft.templateName)?.language})`}
                </Typography>
              )}
            </Box>
            {selectedTemplateData && (
              <Box
                sx={{
                  mt: 2,
                  '& .MuiPaper-root': { p: 1.5, position: 'static' },
                  '& .MuiPaper-root > div': { minHeight: 'auto !important' },
                }}
              >
                <PreviewTemplate
                  cardTitle="Vista Previa de la Plantilla"
                  headerEnabled={selectedTemplateData.components.some((c) => c.type === 'HEADER')}
                  headerText={selectedTemplateData.components.find((c) => c.type === 'HEADER')?.text || null}
                  headerFormat={
                    (selectedTemplateData.components.find((c) => c.type === 'HEADER')?.format as
                      | 'TEXT'
                      | 'IMAGE'
                      | 'VIDEO'
                      | 'DOCUMENT'
                      | null) || null
                  }
                  headerFileUrl={
                    selectedTemplateData.components.find((c) => c.type === 'HEADER' && c.format === 'IMAGE')?.example
                      ?.header_handle?.[0] || null
                  }
                  bodyText={selectedTemplateData.components.find((c) => c.type === 'BODY')?.text || ''}
                  bodyVariableExamples={bodyVariableExamples}
                  footerEnabled={selectedTemplateData.components.some((c) => c.type === 'FOOTER')}
                  footerText={selectedTemplateData.components.find((c) => c.type === 'FOOTER')?.text || null}
                  buttons={selectedTemplateData.components.find((c) => c.type === 'BUTTONS')?.buttons || null}
                />
              </Box>
            )}
          </>
        )}
        {followUpConfigDraft.prompt !== null && isEditing && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: 'text.secondary',
              }}
            >
              Plantillas para elegir (opcional):
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1,
              }}
            >
              Si se especifican plantillas, el sistema elegirá la más adecuada para cada cliente.
            </Typography>
            {followUpConfigDraft.templatesToChoose && followUpConfigDraft.templatesToChoose.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                {followUpConfigDraft.templatesToChoose.map((template) => (
                  <Chip
                    key={`template-${template}`}
                    label={template}
                    onDelete={() => {
                      if (setFollowUpConfigDraft) {
                        const newTemplates = followUpConfigDraft.templatesToChoose?.filter((t) => t !== template) || [];
                        setFollowUpConfigDraft({
                          ...followUpConfigDraft,
                          templatesToChoose: newTemplates.length > 0 ? newTemplates : null,
                        });
                      }
                    }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Agregar plantilla del negocio</InputLabel>
              <Select
                value=""
                onChange={(e) => {
                  const selectedTemplateName = e.target.value;
                  if (
                    selectedTemplateName &&
                    !followUpConfigDraft.templatesToChoose?.includes(selectedTemplateName) &&
                    setFollowUpConfigDraft
                  ) {
                    const newTemplates = [...(followUpConfigDraft.templatesToChoose || []), selectedTemplateName];
                    setFollowUpConfigDraft({
                      ...followUpConfigDraft,
                      templatesToChoose: newTemplates,
                    });
                  }
                }}
                label="Agregar plantilla del negocio"
              >
                <MenuItem value="">
                  <em>Seleccionar plantilla del negocio</em>
                </MenuItem>
                {templates
                  .filter((template) => !followUpConfigDraft.templatesToChoose?.includes(template.name))
                  .map((template) => (
                    <MenuItem
                      key={template.id}
                      value={template.name}
                      disabled={!isTemplateSelectable(template.status)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}
                    >
                      <span>
                        {template.name} ({template.language})
                      </span>
                      <TemplateStatusTag status={template.status} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {followUpConfigDraft.prompt !== null &&
          !isEditing &&
          followUpConfigDraft.templatesToChoose &&
          followUpConfigDraft.templatesToChoose.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: 'text.secondary',
                }}
              >
                Templates para elegir:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {followUpConfigDraft.templatesToChoose.map((template) => (
                  <Chip key={`template-display-${template}`} label={template} color="primary" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
      </CardContent>
    </Card>
  );
};

export default TemplatesSection;
